import { z } from "zod";

const uuidSchema = z.string().uuid();
const offsetDateTimeSchema = z.string().datetime({ offset: true });

export const scheduleDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Expected an ISO date (YYYY-MM-DD)")
  .refine((value) => {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, "Expected a valid calendar date");

export const scheduleWeekStartSchema = scheduleDateSchema.refine(
  (value) => new Date(`${value}T00:00:00.000Z`).getUTCDay() === 1,
  "Schedule weeks must start on Monday",
);

export const scheduleEntryKindSchema = z.enum(["duty", "rest_day"]);

const availabilityNoteSchema = z.string().trim().max(500).optional();

const dutyScheduleEntryInputSchema = z
  .object({
    kind: z.literal("duty"),
    scheduleDate: scheduleDateSchema,
    dutyStart: offsetDateTimeSchema,
    dutyEnd: offsetDateTimeSchema,
    availabilityNote: availabilityNoteSchema,
  })
  .strict();

const restDayScheduleEntryInputSchema = z
  .object({
    kind: z.literal("rest_day"),
    scheduleDate: scheduleDateSchema,
    availabilityNote: availabilityNoteSchema,
  })
  .strict();

/** One schedule cell. Operating hours are intentionally not encoded here. */
export const scheduleEntryInputSchema = z
  .discriminatedUnion("kind", [dutyScheduleEntryInputSchema, restDayScheduleEntryInputSchema])
  .superRefine((entry, context) => {
    if (entry.kind === "duty" && Date.parse(entry.dutyEnd) <= Date.parse(entry.dutyStart)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["dutyEnd"],
        message: "Duty end must be after duty start",
      });
    }
  });

export const scheduleDraftEntryInputSchema = z.object({
  staffId: uuidSchema,
  entry: scheduleEntryInputSchema,
});

export const createScheduleDraftSchema = z
  .object({
    weekStart: scheduleWeekStartSchema,
    sourceVersionId: uuidSchema.optional(),
    changeReason: z.string().trim().min(1).max(500),
    entries: z.array(scheduleDraftEntryInputSchema).optional(),
  })
  .strict();

const optimisticScheduleWriteSchema = z
  .object({
    scheduleVersionId: uuidSchema,
    expectedUpdatedAt: offsetDateTimeSchema,
  })
  .strict();

export const upsertScheduleEntrySchema = optimisticScheduleWriteSchema.extend({
  staffId: uuidSchema,
  entry: scheduleEntryInputSchema,
});

export const removeScheduleEntrySchema = optimisticScheduleWriteSchema.extend({
  entryId: uuidSchema,
});

export const applySchedulePatternSchema = optimisticScheduleWriteSchema.extend({
  staffIds: z.array(uuidSchema).min(1),
  entries: z.array(scheduleEntryInputSchema).min(1),
});

export const validateScheduleDraftSchema = optimisticScheduleWriteSchema;

/** Publication authority and conflict resolution are server policy, never request fields. */
export const publishScheduleSchema = optimisticScheduleWriteSchema.extend({
  changeReason: z.string().trim().min(1).max(500),
});

export const scheduleHistoryQuerySchema = z.object({
  weekStart: scheduleWeekStartSchema.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const scheduleBlockerCodeSchema = z.enum([
  "ENTRY_OUTSIDE_WEEK",
  "INVALID_DUTY_RANGE",
  "OVERLAPPING_SHIFT",
  "REST_DAY_CONFLICT",
  "STAFF_NOT_SCHEDULABLE",
  "WRONG_HUB",
  "CONFIRMED_COMMITMENT",
]);

export const scheduleBlockerSchema = z.object({
  code: scheduleBlockerCodeSchema,
  staffId: uuidSchema.optional(),
  scheduleDate: scheduleDateSchema.optional(),
  entryId: uuidSchema.optional(),
  bookingId: uuidSchema.optional(),
});

export const scheduleValidationResultSchema = z
  .object({
    valid: z.boolean(),
    evaluatedAt: offsetDateTimeSchema,
    blockers: z.array(scheduleBlockerSchema),
  })
  .superRefine((result, context) => {
    if (result.valid !== (result.blockers.length === 0)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["valid"],
        message: "Valid must match whether blockers are empty",
      });
    }
  });

export const staffScheduleEntrySchema = z.object({
  id: uuidSchema,
  scheduleVersionId: uuidSchema,
  staffId: uuidSchema,
  scheduleDate: scheduleDateSchema,
  kind: scheduleEntryKindSchema,
  dutyStart: offsetDateTimeSchema.nullable(),
  dutyEnd: offsetDateTimeSchema.nullable(),
  availabilityNote: z.string().nullable(),
  createdAt: offsetDateTimeSchema,
  updatedAt: offsetDateTimeSchema,
});

export const staffScheduleVersionSchema = z.object({
  id: uuidSchema,
  hubId: uuidSchema,
  weekStart: scheduleWeekStartSchema,
  versionNumber: z.number().int().positive(),
  sourceVersionId: uuidSchema.nullable(),
  changeReason: z.string().min(1),
  createdBy: uuidSchema,
  createdAt: offsetDateTimeSchema,
  updatedAt: offsetDateTimeSchema,
  publishedBy: uuidSchema.nullable(),
  publishedAt: offsetDateTimeSchema.nullable(),
  status: z.enum(["draft", "published"]),
  entries: z.array(staffScheduleEntrySchema),
});

export const scheduleHistorySchema = z.object({
  versions: z.array(staffScheduleVersionSchema),
  nextCursor: z.string().nullable(),
});

export const staleScheduleVersionErrorSchema = z.object({
  code: z.literal("STALE_VERSION"),
  scheduleVersionId: uuidSchema,
  expectedUpdatedAt: offsetDateTimeSchema,
  actualUpdatedAt: offsetDateTimeSchema,
});

export const scheduleErrorCodeSchema = z.enum([
  "SCHEDULE_NOT_FOUND",
  "SCHEDULE_ALREADY_PUBLISHED",
  "STALE_VERSION",
  "SCHEDULE_VALIDATION_FAILED",
]);

export type ScheduleDate = z.infer<typeof scheduleDateSchema>;
export type ScheduleEntryKind = z.infer<typeof scheduleEntryKindSchema>;
export type ScheduleEntryInput = z.infer<typeof scheduleEntryInputSchema>;
export type ScheduleDraftEntryInput = z.infer<typeof scheduleDraftEntryInputSchema>;
export type CreateScheduleDraftInput = z.infer<typeof createScheduleDraftSchema>;
export type UpsertScheduleEntryInput = z.infer<typeof upsertScheduleEntrySchema>;
export type RemoveScheduleEntryInput = z.infer<typeof removeScheduleEntrySchema>;
export type ApplySchedulePatternInput = z.infer<typeof applySchedulePatternSchema>;
export type ValidateScheduleDraftInput = z.infer<typeof validateScheduleDraftSchema>;
export type PublishScheduleInput = z.infer<typeof publishScheduleSchema>;
export type ScheduleHistoryQuery = z.infer<typeof scheduleHistoryQuerySchema>;
export type ScheduleBlockerCode = z.infer<typeof scheduleBlockerCodeSchema>;
export type ScheduleBlocker = z.infer<typeof scheduleBlockerSchema>;
export type ScheduleValidationResult = z.infer<typeof scheduleValidationResultSchema>;
export type StaffScheduleEntry = z.infer<typeof staffScheduleEntrySchema>;
export type StaffScheduleVersion = z.infer<typeof staffScheduleVersionSchema>;
export type ScheduleHistory = z.infer<typeof scheduleHistorySchema>;
export type StaleScheduleVersionError = z.infer<typeof staleScheduleVersionErrorSchema>;
export type ScheduleErrorCode = z.infer<typeof scheduleErrorCodeSchema>;
